/**
 * Settings → Team (HOF-627 §12, slice T1) — the contract and pure helpers.
 *
 * `GET /api/v1/farm/members` answers from the caller's seat: owners and farm managers see the
 * team and may manage it (`can_manage`, `assignable_roles`); staff, bookkeepers and viewers are
 * refused; a support (impersonated) view manages as the viewed seat can (HOF-708). Phone and email are each
 * person's own login and are never editable here; the farm's name for a person is the seat's
 * `display_label`.
 */
import api from '../../services/api';

export const FARM_MEMBERS_PATH = '/v1/farm/members';
export const FARM_INVITATIONS_PATH = '/v1/farm/invitations';

/** The role catalogue (R4), in the order the UI lists it. Labels are i18n `roles.*`. */
export const TEAM_ROLES = ['owner', 'farm_manager', 'staff', 'bookkeeper', 'viewer'] as const;
export type TeamRole = (typeof TEAM_ROLES)[number];

/**
 * The labour seats (HOF-696 D1, HOF-697): the labour access sync creates, binds, suspends and
 * removes them from the employee card. The one thing the Team page may change on one is which of
 * the two it is — Arbeitskraft ⇄ Vorarbeiter (`members.manage`); an office role is never offered.
 */
export const LABOUR_SEAT_ROLES = ['worker', 'foreman'] as const;
export type LabourSeatRole = (typeof LABOUR_SEAT_ROLES)[number];

/** Roles a seat can HOLD — the assignable office roles plus the labour seats. */
export const DISPLAY_ROLES = [...TEAM_ROLES, ...LABOUR_SEAT_ROLES] as const;
export type SeatRole = (typeof DISPLAY_ROLES)[number];

export function isLabourSeatRole(role: string): role is LabourSeatRole {
    return (LABOUR_SEAT_ROLES as readonly string[]).includes(role);
}

export interface TeamMember {
    user_id: string;
    first_name: string | null;
    last_name: string | null;
    phone: string;
    email: string | null;
    role: SeatRole;
    status: 'active' | 'suspended';
    display_label: string | null;
    is_you: boolean;
    /** `labour_sync`: the seat follows the employee card (HOF-696 D1) — the Team APIs refuse it. */
    managed_by?: 'labour_sync' | null;
}

/** A seat the labour access sync manages (HOF-696 D1): no office role, status, label or removal here. */
export function isManagedSeat(member: Pick<TeamMember, 'role' | 'managed_by'>): boolean {
    return isLabourSeatRole(member.role) || Boolean(member.managed_by);
}

export interface TeamInvitation {
    invite_id: string;
    destination: string;
    channel: 'phone' | 'email';
    role: TeamRole;
    status: 'pending';
    expires_at: string;
    /** `legacy`: an employee invite from the old invite form — revoked there, not here. */
    source: 'farm' | 'legacy';
}

export interface FarmMembersResponse {
    farm: { tenant_id: string; farm_name: string | null };
    members: TeamMember[];
    invitations: TeamInvitation[];
    can_manage: boolean;
    can_invite: boolean;
    assignable_roles: TeamRole[];
    viewer_is_impersonating: boolean;
}

export interface MemberPatch {
    role?: TeamRole | LabourSeatRole;
    status?: 'active' | 'suspended';
    display_label?: string | null;
}

/** Every `settings.team.*` key the section renders; the locale test pins de/en parity on it. */
export const TEAM_SETTINGS_KEYS = [
    'title',
    'subtitle',
    'loading',
    'loadFailed',
    'you',
    'suspended',
    'invitationPending',
    'invitationExpires',
    'selfServiceHint',
    'roleLabel',
    'suspend',
    'activate',
    'remove',
    'labelPlaceholder',
    'invite',
    'invitePlaceholder',
    'inviteRole',
    'revoke',
    'legacyInvitation',
    'saveFailed',
    'saved',
    'lastOwner',
    'alreadyInvited',
    'emailInvitation',
    'emailInviteHint',
    'editLabel',
    'save',
    'cancel',
    'editInvitation',
    'renewInvitation',
    'renewed',
    'accountInactive',
    'inviteOpen',
    'inviteDescription',
    'membersTitle',
    'readOnlyHint',
    'invitationsTitle',
    'removeTitle',
    'removeDescription',
    'actionsFor',
    'invitationActionsFor',
    'legacyBadge',
    'managedByLabour',
    'managedByLabourRefused',
] as const;

export async function fetchFarmMembers(): Promise<FarmMembersResponse> {
    const response = await api.get<FarmMembersResponse>(FARM_MEMBERS_PATH, { withCredentials: true });
    return response.data;
}

export async function updateMember(userId: string, patch: MemberPatch): Promise<TeamMember> {
    const response = await api.patch<TeamMember>(`${FARM_MEMBERS_PATH}/${userId}`, patch, { withCredentials: true });
    return response.data;
}

export async function removeMember(userId: string): Promise<void> {
    await api.delete(`${FARM_MEMBERS_PATH}/${userId}`, { withCredentials: true });
}

export async function inviteToFarm(destination: string, role: TeamRole): Promise<TeamInvitation> {
    const response = await api.post<TeamInvitation>(
        FARM_INVITATIONS_PATH,
        { destination, role },
        { withCredentials: true }
    );
    return response.data;
}

export interface InvitationPatch {
    destination?: string;
    role?: TeamRole;
}

/** Correct a pending invitation — its destination is the farm's until it is accepted (§12 rule 1). */
export async function updateInvitation(inviteId: string, patch: InvitationPatch): Promise<TeamInvitation> {
    const response = await api.patch<TeamInvitation>(`${FARM_INVITATIONS_PATH}/${inviteId}`, patch, {
        withCredentials: true,
    });
    return response.data;
}

/** "Resend": restart its validity. Nothing is sent — the person claims it by signing in with that destination. */
export async function renewInvitation(inviteId: string): Promise<TeamInvitation> {
    const response = await api.post<TeamInvitation>(`${FARM_INVITATIONS_PATH}/${inviteId}/renew`, {}, {
        withCredentials: true,
    });
    return response.data;
}

export async function revokeInvitation(inviteId: string): Promise<void> {
    await api.delete(`${FARM_INVITATIONS_PATH}/${inviteId}`, { withCredentials: true });
}

export function memberDisplayName(member: Pick<TeamMember, 'first_name' | 'last_name' | 'phone'>): string {
    const name = `${member.first_name ?? ''} ${member.last_name ?? ''}`.trim();
    return name || member.phone;
}

export function roleLabelKey(role: SeatRole): `roles.${SeatRole}` {
    return `roles.${role}`;
}

export const ROLE_LABEL_FALLBACKS: Record<SeatRole, string> = {
    owner: 'Inhaber',
    farm_manager: 'Betriebsleitung',
    staff: 'Mitarbeiter',
    bookkeeper: 'Buchhaltung',
    viewer: 'Nur lesen',
    worker: 'Arbeitskraft',
    foreman: 'Vorarbeiter',
};

/**
 * What each role may do, in one line — shown wherever a role is chosen. Mirrors
 * ``ROLE_PERMISSIONS`` in ``backend/src/core/access/catalogue.py``; change both together.
 */
export function roleDescriptionKey(role: TeamRole | LabourSeatRole): `roleDescriptions.${TeamRole | LabourSeatRole}` {
    return `roleDescriptions.${role}`;
}

export const ROLE_DESCRIPTION_FALLBACKS: Record<TeamRole | LabourSeatRole, string> = {
    owner: 'Alles, was die Betriebsleitung darf – dazu Inhaber ernennen und den Betrieb löschen.',
    farm_manager: 'Führt den Betrieb: Dokumente, Betriebsdaten, Finanzen, Verbindungen und das Team.',
    staff: 'Lädt Dokumente hoch und pflegt die Betriebsdaten. Sieht keine Finanzen und verwaltet kein Team.',
    bookkeeper: 'Sieht und bearbeitet die Finanzen. Lädt keine Dokumente hoch und verwaltet kein Team.',
    viewer: 'Sieht den Betrieb ohne Finanzen und kann den Assistenten fragen. Ändert nichts.',
    worker: 'Trägt in der Arbeit-App die eigene Arbeitszeit ein. Sieht sonst nichts vom Betrieb.',
    foreman:
        'Trägt die eigene Zeit ein, sieht die Woche des Teams und erfasst oder korrigiert Zeiten für andere. Sieht sonst nichts vom Betrieb.',
};

/** The server's refusal code, when it sent one (`{detail: {code}}`). */
export function refusalCode(error: unknown): string | undefined {
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
    return detail && typeof detail === 'object' ? (detail as { code?: string }).code : undefined;
}
